package nl.volleyplanner.web.base.component

import kotlinx.browser.document
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.html.ButtonType
import kotlinx.html.TagConsumer
import kotlinx.html.button
import kotlinx.html.div
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.web.IdGenerator
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.base.HTMLComponent
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement

/**
 * A reusable toggle button group component that manages a shared state of selected keys.
 * - Renders a group button to toggle all on/off for this group.
 * - Renders a button per item that toggles inclusion in the shared state.
 *
 * Usage:
 *   val state = MutableStateFlow<Set<String>>(emptySet())
 *   component(ToggleButtonGroup("Teams", state, listOf(ToggleButton("H1", "H1", true))))
 */
class ToggleButtonGroup<T>(
    private val groupLabel: String,
    private val sharedState: MutableStateFlow<Set<T>>,
    private val items: List<ToggleButton<T>>
) : HTMLComponent() {
    private val logger = Logger.getLogger("nl.volleyplanner.web.base.component.ToggleButtonGroup")

    data class ToggleButton<T>(
        val label: String,
        val key: T,
        val defaultState: Boolean
    ) {
        val itemId: String = "filter-btn-${IdGenerator.nextId()}"
    }

    private val groupId = groupLabel.replace(" ", "-")

    init {
        // Initialize defaults
        sharedState.value = sharedState.value + items.filter { it.defaultState }.map { it.key }.toSet()
    }

    private fun setItemState(item: ToggleButton<T>, active: Boolean) {
        val btnElement = document.querySelector("#${item.itemId}") as HTMLButtonElement
        if (!active) btnElement.classList.add("active") else btnElement.classList.remove("active")

        sharedState.value = if (active) sharedState.value + item.key else sharedState.value - item.key
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div(classes = "btn-group me-2") {
            attributes["role"] = "group"
            attributes["aria-label"] = "$groupLabel filter"

            // Group-level toggle button
            run {
                val allKeys = items.map { it.key }.toSet()
                val cls = "btn btn-primary" + if (!sharedState.value.containsAll(allKeys)) " active" else ""
                button(classes = cls) {
                    type = ButtonType.button
                    +groupLabel

                    onClickFunction = { event ->
                        event.preventDefault()
                        val buttonElement = event.target as HTMLButtonElement
                        val anyActive = sharedState.value.any { key -> items.any { it.key == key } }
                        if (anyActive) {
                            buttonElement.classList.add("active")
                            items.forEach { item -> setItemState(item, false) }
                        } else {
                            buttonElement.classList.remove("active")
                            items.forEach { item -> setItemState(item, true) }
                        }
                    }
                }
            }

            // Individual item buttons
            items.forEach { item ->
                val initialClasses = "btn btn-primary" + if (!item.defaultState) " active" else ""
                button(classes = initialClasses) {
                    attributes["data-vp-filtergroup"] = groupId
                    type = ButtonType.button
                    attributes["id"] = item.itemId

                    onClickFunction = { event ->
                        event.preventDefault()
                        val isActive = sharedState.value.contains(item.key)
                        setItemState(item, !isActive)
                    }

                    +item.label
                }
            }
        }
    }

    override suspend fun getState() = null
}
